/**
 * InputNumber - Simple Number Input with Custom Controls
 *
 * Lightweight number input component with optional +/- button controls.
 * Built on top of native HTML `<input type="number">` with custom styling.
 *
 * **Use Cases**:
 * - Simple number fields in CRUD forms
 * - Quick quantity inputs
 * - When you need basic number input without Chakra UI stepper styling
 *
 * **When to Use NumberInput Instead**:
 * - If you need full Chakra UI theming and styling
 * - If you want native Chakra NumberInput.Root API
 * - For complex form fields with react-hook-form integration
 *
 * @see NumberInput (number-input.tsx) for Chakra UI native wrapper
 * @module input-number
 */

import * as React from 'react';
import { FiMinus, FiPlus } from 'react-icons/fi';

import { Input, InputGroup, InputLeftElement, InputRightElement } from './input';
import { IconButton } from './icon-button';

export interface InputNumberProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Input>, 'type' | 'onChange'> {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  onChange?: (value: number | undefined) => void;
  showControls?: boolean;
}

export const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (
    { value, min, max, step = 1, precision = 0, onChange, showControls = true, disabled, ...props },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState<string>(
      value !== undefined ? value.toFixed(precision) : ''
    );

    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value.toFixed(precision));
      } else {
        setInternalValue('');
      }
    }, [value, precision]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      if (newValue === '') {
        onChange?.(undefined);
      } else {
        const numValue = parseFloat(newValue);
        if (!isNaN(numValue)) {
          const clampedValue = clamp(numValue, min, max);
          onChange?.(clampedValue);
        }
      }
    };

    const handleIncrement = () => {
      const currentValue = value ?? 0;
      const newValue = clamp(currentValue + step, min, max);
      onChange?.(newValue);
    };

    const handleDecrement = () => {
      const currentValue = value ?? 0;
      const newValue = clamp(currentValue - step, min, max);
      onChange?.(newValue);
    };

    const clamp = (val: number, minVal?: number, maxVal?: number) => {
      if (minVal !== undefined && val < minVal) return minVal;
      if (maxVal !== undefined && val > maxVal) return maxVal;
      return val;
    };

    if (!showControls) {
      return (
        <Input
          ref={ref}
          type="number"
          value={internalValue}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          {...props}
        />
      );
    }

    return (
      <InputGroup>
        <InputLeftElement>
          <IconButton
            size="sm"
            variant="ghost"
            onClick={handleDecrement}
            disabled={disabled || (min !== undefined && (value ?? 0) <= min)}
            aria-label="Decrease"
            icon={<FiMinus />}
          />
        </InputLeftElement>

        <Input
          ref={ref}
          type="number"
          value={internalValue}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          textAlign="center"
          paddingLeft="48px"
          paddingRight="48px"
          {...props}
        />

        <InputRightElement>
          <IconButton
            size="sm"
            variant="ghost"
            onClick={handleIncrement}
            disabled={disabled || (max !== undefined && (value ?? 0) >= max)}
            aria-label="Increase"
            icon={<FiPlus />}
          />
        </InputRightElement>
      </InputGroup>
    );
  }
);

InputNumber.displayName = 'InputNumber';
